import { useEffect, useSyncExternalStore } from 'react';
import type { OnboardingNudgeKey } from '@wundo/shared/onboardingNudge.ts';

/**
 * Which picture a quick action carries.
 *
 * A name rather than the icon itself: the page says what kind of thing it is
 * offering, the drawer decides what that looks like — and an offer made of
 * plain data can be compared by what it says instead of by which render made it.
 */
export type ChatQuickActionIcon =
  | 'tips'
  | 'roleplay'
  | 'check'
  | 'week'
  | 'prepare'
  | 'recap'
  | 'people';

/**
 * What clicking a quick action does. `send` — the default, and what every pill
 * did before there was a choice — says the words on it. `focus` says nothing at
 * all and puts the caret in the composer: the question has already been asked
 * in the conversation, and a pill that answered it for the user would be words
 * they never wrote. `intake` says nothing either, but first stands the drawer
 * where this page's opening question belongs — the conversation it has already
 * been answered in, or one that does not exist yet — so what the user writes
 * lands there rather than in whichever conversation happens to be running.
 */
export type ChatQuickActionBehavior = 'send' | 'focus' | 'intake';

export interface ChatQuickActionOffer {
  /** Both the label and, when it is clicked, the message that gets sent. */
  text: string;
  icon: ChatQuickActionIcon;
  /** What the click does. Left out on a pill that sends what it says. */
  behavior?: ChatQuickActionBehavior;
}

/**
 * An opening line the page has ready, and where the answer to it goes.
 *
 * The text is the server's own — it becomes the first message of the
 * conversation the answer creates, so the drawer must not write its own version
 * of it. Until then it is drawn and nothing more: no conversation exists, and
 * closing the drawer leaves nothing behind.
 *
 * A page that has one is asking for the drawer's attention, not merely offering
 * something: the drawer stands itself on a blank conversation to show it, once
 * per page, whatever was running before. Nothing running is touched by that —
 * it is still there in the history, one click away.
 */
export interface ChatPageIntake {
  /** The whole opener, rendered by the server in the user's language. */
  opener: string;
  /** The meeting whose intake endpoint the first message goes to. */
  meetingId: string;
  /**
   * The conversation the question has already been answered in, or null while
   * it has only been asked.
   *
   * The server's answer, not the drawer's memory: a page still offering the
   * opener after a reload is a page whose preparation is not written yet, which
   * is not the same thing as a question nobody has answered. With a
   * conversation named, the opener is neither drawn nor answerable — both the
   * drawer and the pill open that conversation instead, and a second answer
   * cannot become a second conversation about one meeting.
   */
  conversationId: string | null;
}

/**
 * An opening line for a conversation that already happened, and where the
 * answer to it goes.
 *
 * The same arrangement as the opener above, and for the same reasons: the text
 * is the server's own — it becomes the first message of the conversation the
 * answer creates — and until then it is drawn and nothing more.
 *
 * The two never stand together. One belongs to the tab about the conversation
 * still to come, this one to the tab about the sitting that is over and that
 * Wundo has nothing to write up from.
 */
export interface ChatPageDebrief {
  /** The whole opener, rendered by the server in the user's language. */
  opener: string;
  /** The meeting whose debrief endpoint the first message goes to. */
  meetingId: string;
  /**
   * The conversation the question is already being answered in, or null while
   * it has only been asked.
   *
   * The server's answer, not the drawer's memory: the page goes on offering the
   * opener until a verslag exists, and answering is not what makes one exist.
   * With a conversation named, the opener is neither drawn nor answerable —
   * both the drawer and the pill open that conversation instead, and a second
   * answer cannot become a second conversation about one meeting.
   */
  conversationId: string | null;
}

/**
 * What was said in the previous conversation, and where talking about it goes.
 *
 * The same arrangement as the opener above, and for the same reasons: the text
 * is the server's own — it becomes the first message of the conversation this
 * creates — and until somebody says something it is drawn and nothing more.
 * Only the heading and the words on the link are the page's own; those are
 * chrome, and never leave the browser.
 *
 * The two never stand together. A page has an opener only when there is nothing
 * to prepare from, and a previous conversation is something to prepare from.
 */
export interface ChatPageRecap {
  /** The summary, rendered by the server in the user's language. */
  text: string;
  /** The meeting whose recap endpoint the first message goes to. */
  meetingId: string;
  /** What the card is called, the date it is about included. */
  title: string;
  /** The words on the link to the conversation this summarises. */
  linkText: string;
  /** The meeting that link opens — the previous one, not this page's. */
  linkMeetingId: string;
  /**
   * The conversation this summary has already been taken up in, or null while
   * it has only been shown.
   *
   * The server's answer, not the drawer's memory: the page goes on offering the
   * summary for as long as there is one, which is forever, so this is the only
   * thing that says the card has been answered. With a conversation named the
   * card is neither drawn nor answerable — the drawer opens that conversation
   * instead, and a second answer cannot become a second conversation about one
   * meeting.
   */
  conversationId: string | null;
}

/**
 * The one thing this page recommends to somebody who has not done it yet.
 *
 * The backend's answer, whole: which nudge applies here, whether the window is
 * open, whether it has been refused often enough to stop asking — none of that
 * is the page's judgement. The page only says where it is standing, and passes
 * on what comes back.
 *
 * It creates nothing and is stored nowhere. Refusing it or doing the thing
 * simply takes it away.
 */
export interface ChatPageNudge {
  key: OnboardingNudgeKey;
  /** The meeting it is about, on the two nudges that are about one. */
  meetingId?: string;
  /**
   * Whether this person has never been shown this nudge before. The drawer
   * opens itself for a first time and stays as it was for every one after.
   */
  firstTime: boolean;
}

/**
 * What a page offers the chat while it is on screen.
 *
 * A page that can be specific says so in the drawer's subtitle and in the row of
 * things worth asking above the composer. A page that cannot leaves the chat
 * exactly the general chat it is everywhere else — same drawer, same pills, the
 * ones anybody would want on any page.
 */
export interface ChatPageOffer {
  /** The line under the drawer's title. Left out on pages that have none. */
  line?: string;
  /** Pills above the composer, in order. Left out to show none. */
  quickActions?: ChatQuickActionOffer[];
  /**
   * An opener the page has ready for an empty drawer, and the meeting the first
   * answer belongs to. Left out on every page that has nothing to open with —
   * which is all of them but a meeting Wundo cannot prepare on its own.
   */
  intake?: ChatPageIntake;
  /**
   * An opener for an empty drawer beside a meeting that is over and that Wundo
   * has nothing to write the verslag from. Left out everywhere else.
   */
  debrief?: ChatPageDebrief;
  /**
   * The summary of the previous conversation, for an empty drawer beside a
   * meeting that has had one. Left out everywhere else.
   */
  recap?: ChatPageRecap;
  /**
   * The one thing worth recommending here to somebody still finding their way.
   * Last of the three: what Wundo has to ask about the meeting on screen, and
   * what was said about it last time, are both about the page — a nudge is
   * about the person.
   */
  nudge?: ChatPageNudge;
}

/*
 * One drawer, one page behind it, so one answer — held in the module rather
 * than in a context, for the same reason the chat's anchor is: the page that
 * knows this and the drawer that shows it are nowhere near each other in the
 * tree.
 */
let current: ChatPageOffer | null = null;
/* What the offer says, so an identical one published again changes nothing. */
let currentKey: string | null = null;

const listeners = new Set<() => void>();

/** What the page on screen offers, or null where it offers nothing. */
export function currentChatPageOffer(): ChatPageOffer | null {
  return current;
}

/** Say what this page offers. Pages do this through the hook below. */
export function setChatPageOffer(offer: ChatPageOffer | null): void {
  current = offer;
  currentKey = offer === null ? null : JSON.stringify(offer);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Publish this page's offer for as long as the page is on screen.
 *
 * Compared by what it says rather than by which object said it, so a page may
 * build its offer inline: republishing on every render would put the drawer in
 * a loop, and asking every page to memoise correctly is asking for that loop.
 *
 * Cleared on the way out, because leaving the page has to be said as clearly as
 * arriving on it — a line about a meeting under the settings page is worse than
 * no line at all. Only cleared while the offer still is this page's own: a page
 * that has already handed over to another must not wipe the newer one.
 */
export function usePublishChatPageOffer(offer: ChatPageOffer): void {
  const key = JSON.stringify(offer);

  useEffect(() => {
    setChatPageOffer(JSON.parse(key) as ChatPageOffer);

    return () => {
      if (currentKey === key) setChatPageOffer(null);
    };
  }, [key]);
}

/** The offer, as the drawer reads it: reactively, from wherever it is drawn. */
export function useChatPageOffer(): ChatPageOffer | null {
  return useSyncExternalStore(subscribe, currentChatPageOffer, () => null);
}
